import {
  useRef,
  useEffect,
  useLayoutEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, Typography } from "@mui/material";
import { ErrorOutlineOutlined as ErrorOutlineOutlinedIcon } from "@mui/icons-material";
import { colors } from "../theme/colors";
import { Message } from "./CustomMessage";
import { TypingIndicator } from "./CustomTypingIndicator";
import CustomLoading from "../Loading/CustomLoading";
import CustomButton from "../Buttons/Button/CustomButton";
import type { Message as MessageType } from "./AIChatTypes";

interface ChatMessageAreaProps {
  messages: MessageType[];
  primaryColor?: string;
  isSending?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  isLoadingInitial?: boolean;
  loadError?: string | null;
  onRetry?: () => void;
  isExpanded?: boolean;
  onScrollPositionChange?: (showButton: boolean) => void;
  onConfirmAction?: (messageId: string, actionId: string) => void;
  onRejectAction?: (messageId: string, actionId: string) => void;
}

export interface ChatMessageAreaRef {
  scrollToBottom: () => void;
}

export const ChatMessageArea = forwardRef<
  ChatMessageAreaRef,
  ChatMessageAreaProps
>(
  (
    {
      messages,
      primaryColor = colors.primary.main,
      isSending = false,
      hasMore = false,
      isLoadingMore = false,
      onLoadMore,
      isLoadingInitial = false,
      loadError = null,
      onRetry,
      isExpanded = false,
      onScrollPositionChange,
      onConfirmAction,
      onRejectAction,
    },
    ref
  ) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const topObserverRef = useRef<HTMLDivElement>(null);

    const displayedMessages = messages;

    // 一番下にスクロールする関数を外部に公開
    useImperativeHandle(ref, () => ({
      scrollToBottom: () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      },
    }));

    const isInitialMount = useRef(true);
    const hasScrolledInitially = useRef(false);
    const previousMessageCount = useRef(messages.length);
    const previousFirstMessageId = useRef<string | null>(null);
    const [isScrolled, setIsScrolled] = useState(false);

    useLayoutEffect(() => {
      if (messages.length === 0) {
        hasScrolledInitially.current = false;
        previousFirstMessageId.current = null;
        requestAnimationFrame(() => {
          setIsScrolled(false);
        });
        return;
      }

      if (
        !hasScrolledInitially.current &&
        messages.length > 0 &&
        messagesEndRef.current
      ) {
        messagesEndRef.current.scrollIntoView({ behavior: "auto" });
        hasScrolledInitially.current = true;
        isInitialMount.current = false;
        previousFirstMessageId.current = messages[0]?.id || null;

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setIsScrolled(true);
          });
        });
      }
    }, [messages.length]);

    useEffect(() => {
      if (!hasScrolledInitially.current) {
        previousMessageCount.current = messages.length;
        previousFirstMessageId.current = messages[0]?.id || null;
        return;
      }

      if (messages.length !== previousMessageCount.current) {
        const currentFirstMessageId = messages[0]?.id || null;
        const isLoadingHistory =
          currentFirstMessageId !== previousFirstMessageId.current &&
          previousFirstMessageId.current !== null;

        // 履歴読み込みでない場合のみ（新規メッセージの場合）、一番下にスクロール
        if (!isLoadingHistory) {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }

        previousMessageCount.current = messages.length;
        previousFirstMessageId.current = currentFirstMessageId;
      }
    }, [messages.length]);

    // 履歴読み込み時のスクロール位置保存
    const scrollHeightBeforeLoad = useRef<number>(0);
    const isLoadingHistory = useRef(false);

    // Intersection observer for loading more messages from backend
    useEffect(() => {
      if (!onLoadMore) return;

      const observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting && hasMore && !isLoadingMore) {
            // スクロール位置を保存
            scrollHeightBeforeLoad.current =
              messagesContainerRef.current?.scrollHeight || 0;
            isLoadingHistory.current = true;

            // バックエンドから追加メッセージを読み込み
            onLoadMore();
          }
        },
        {
          root: messagesContainerRef.current,
          threshold: 0.1,
        }
      );

      if (topObserverRef.current) {
        observer.observe(topObserverRef.current);
      }

      return () => {
        if (topObserverRef.current) {
          observer.unobserve(topObserverRef.current);
        }
      };
    }, [hasMore, isLoadingMore, onLoadMore]);

    // 履歴読み込み完了後のスクロール位置調整
    useEffect(() => {
      if (
        isLoadingHistory.current &&
        !isLoadingMore &&
        messagesContainerRef.current
      ) {
        requestAnimationFrame(() => {
          if (
            messagesContainerRef.current &&
            scrollHeightBeforeLoad.current > 0
          ) {
            const newScrollHeight = messagesContainerRef.current.scrollHeight;
            const scrollDiff = newScrollHeight - scrollHeightBeforeLoad.current;
            messagesContainerRef.current.scrollTop += scrollDiff;

            isLoadingHistory.current = false;
            scrollHeightBeforeLoad.current = 0;
          }
        });
      }
    }, [isLoadingMore, messages.length]);

    // スクロール位置を監視して、一番下にいない時にボタンを表示
    useEffect(() => {
      const container = messagesContainerRef.current;
      if (!container) return;

      const checkScrollPosition = () => {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        // 一番下から100px以上離れたらボタンを表示
        const shouldShow = distanceFromBottom > 100;
        onScrollPositionChange?.(shouldShow);
      };

      // 初期チェック
      checkScrollPosition();

      container.addEventListener("scroll", checkScrollPosition);
      return () => container.removeEventListener("scroll", checkScrollPosition);
    }, [messages.length, isScrolled, onScrollPositionChange]); // メッセージが変わるたびに再評価

    // 会話0件のときの状態表示:
    // - 取得中: 中央にスピナー
    // - 取得失敗: 中央にエラー + 再試行（「開いたのに白画面」を防ぐ）
    const showInitialLoading =
      messages.length === 0 && isLoadingInitial && !loadError;
    const showLoadError = messages.length === 0 && !!loadError;

    return (
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          overscrollBehaviorY: "contain",
          px: 3,
          py: 3,
          position: "relative",
        }}
      >
        {(showInitialLoading || showLoadError) && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 3,
              zIndex: 2,
            }}
          >
            {showLoadError ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                  maxWidth: 360,
                  textAlign: "center",
                }}
              >
                <ErrorOutlineOutlinedIcon
                  sx={{ fontSize: 40, color: colors.border.error }}
                />
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: colors.text.primary,
                  }}
                >
                  会話を読み込めませんでした
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12.5,
                    color: colors.text.secondary,
                    lineHeight: 1.6,
                  }}
                >
                  通信環境をご確認のうえ、もう一度お試しください。
                </Typography>
                {onRetry && (
                  <CustomButton
                    label="再試行"
                    buttonCategory="submit"
                    onClick={onRetry}
                    sx={{ color: colors.text.inverse, mt: 0.5 }}
                  />
                )}
              </Box>
            ) : (
              <CustomLoading loading fullScreen={false} variant="circular" />
            )}
          </Box>
        )}
        <Box
          sx={{
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            maxWidth: "56rem",
            width: "100%",
            opacity: isScrolled ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
          }}
        >
          {/* Loading Indicator */}
          <Box
            ref={topObserverRef}
            sx={{ display: "flex", justifyContent: "center", py: 1 }}
          >
            <CustomLoading
              loading={isLoadingMore}
              fullScreen={false}
              variant="circular"
              size="small"
              color="#989898"
              text="読み込み中..."
            />
          </Box>

          {/* Messages */}
          {displayedMessages.map((message) => (
            <Message
              key={message.id}
              message={message}
              primaryColor={primaryColor}
              enableTypewriter={false}
              showImagePreview={isExpanded}
              onConfirmAction={onConfirmAction}
              onRejectAction={onRejectAction}
            />
          ))}

          {/* Typing Indicator */}
          {isSending && <TypingIndicator primaryColor={primaryColor} />}

          <div ref={messagesEndRef} />
        </Box>
      </Box>
    );
  }
);

ChatMessageArea.displayName = "ChatMessageArea";
